/**
 * Help Service
 * Handles the Help Section catalog API calls: public read access for any
 * authenticated user (categories/articles) and Admin-only CRUD management.
 */
import axios from 'axios';
import BaseApiService from '../base/BaseApiService';
import config from '@config';
import authService from '../authServiceFactory';
import type {
  HelpCategory,
  HelpArticle,
  HelpArticleWithCategory,
  CreateHelpCategoryPayload,
  UpdateHelpCategoryPayload,
  CreateHelpArticlePayload,
  UpdateHelpArticlePayload,
  HelpImageUpload
} from '@app-types';

class HelpService extends BaseApiService {
  /**
   * Get all help categories (public, any authenticated user).
   */
  async getCategories(): Promise<HelpCategory[]> {
    const response = await this.get<{ success: boolean; data: HelpCategory[] }>('/help/categories');
    return response.data;
  }

  /**
   * Get all help articles with category info, pre-sorted (public, any authenticated user).
   */
  async getArticles(): Promise<HelpArticleWithCategory[]> {
    const response = await this.get<{ success: boolean; data: HelpArticleWithCategory[] }>('/help/articles');
    return response.data;
  }

  /**
   * Get a single help article by ID (public, any authenticated user).
   */
  async getArticle(id: number): Promise<HelpArticleWithCategory> {
    const response = await this.get<{ success: boolean; data: HelpArticleWithCategory }>(`/help/articles/${id}`);
    return response.data;
  }

  /**
   * Get all help categories for admin management (Admin only).
   */
  async getAdminCategories(): Promise<HelpCategory[]> {
    const response = await this.get<{ success: boolean; data: HelpCategory[] }>('/admin/help/categories');
    return response.data;
  }

  /**
   * Get all help articles with category info for admin management (Admin only).
   * Same response shape as the public endpoint.
   */
  async getAdminArticles(): Promise<HelpArticleWithCategory[]> {
    const response = await this.get<{ success: boolean; data: HelpArticleWithCategory[] }>('/admin/help/articles');
    return response.data;
  }

  /**
   * Create a new help category (Admin only).
   */
  async createCategory(data: CreateHelpCategoryPayload): Promise<HelpCategory> {
    const response = await this.post<{ success: boolean; data: HelpCategory }>('/admin/help/categories', data);
    return response.data;
  }

  /**
   * Update a help category (Admin only).
   */
  async updateCategory(id: number, data: UpdateHelpCategoryPayload): Promise<HelpCategory> {
    const response = await this.put<{ success: boolean; data: HelpCategory }>(`/admin/help/categories/${id}`, data);
    return response.data;
  }

  /**
   * Delete a help category (Admin only).
   */
  async deleteCategory(id: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/admin/help/categories/${id}`);
  }

  /**
   * Create a new help article (Admin only).
   * Note: unlike the list endpoints, this returns the plain article row
   * (no category_name) - the service layer only has categoryId at this point.
   */
  async createArticle(data: CreateHelpArticlePayload): Promise<HelpArticle> {
    const response = await this.post<{ success: boolean; data: HelpArticle }>('/admin/help/articles', data);
    return response.data;
  }

  /**
   * Update a help article (Admin only).
   * Note: returns the plain article row (no category_name), same as create.
   */
  async updateArticle(id: number, data: UpdateHelpArticlePayload): Promise<HelpArticle> {
    const response = await this.put<{ success: boolean; data: HelpArticle }>(`/admin/help/articles/${id}`, data);
    return response.data;
  }

  /**
   * Delete a help article (Admin only).
   */
  async deleteArticle(id: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/admin/help/articles/${id}`);
  }

  /**
   * Upload an Article Image (Admin only). Works before the article exists:
   * the image stays unattached until a body referencing its id is saved.
   * Special case: uses axios directly with FormData (same as
   * attachmentService.uploadAttachments) - apiClient's JSON default
   * Content-Type would serialize the FormData as JSON.
   */
  async uploadImage(file: File): Promise<HelpImageUpload> {
    const formData = new FormData();
    formData.append('file', file);

    const service = await authService;
    const token = await service.getAccessToken();

    const response = await axios.post<{ success: boolean; data: HelpImageUpload }>(
      `${config.apiBaseUrl}/admin/help/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data.data;
  }
}

export const helpService = new HelpService();
export default helpService;
